<template>
  <v-card>
    <v-row class="mt-2 mx-2 pt-2">
      <v-col cols="5" xs="5" class="text-left mt-2 pt-0">
        <v-text-field v-model="selectedStartDate" dense type="date" label="Start Date"></v-text-field>
      </v-col>
      <v-col cols="5" xs="5" class="text-right mt-2 pt-0">
        <v-text-field v-model="selectedEndDate" dense type="date" label="End Date"></v-text-field>
      </v-col>
      <v-col cols="2" xs="2" class="text-right mt-0 pt-0">
        <spinner v-if="loading" class="text-end"></spinner>
        <v-btn v-else icon small class="primary" @click="getTransactions()">
          <font-awesome-icon icon="fa-solid fa-search" />
        </v-btn>
      </v-col>
    </v-row>

    <v-card-title class="">
      <span class="me-3">({{ filteredTransactions.length }}) Transactions</span>
      <v-spacer></v-spacer>
      <v-text-field v-model="search" append-icon="mdi-magnify" label="Search" single-line hide-details></v-text-field>
    </v-card-title>

    <h3 class="ml-3">Total Contributions: {{ contribution_total }} F CFA</h3>

    <v-data-table
      :headers="headers"
      :items="filteredTransactions"
      item-key="id"
      class="table-rounded"
      :items-per-page="10"
      disable-sort
      :search="search"
    >
      <!-- Nom -->
      <template #[`item.username`]="{ item }">
        <div class="d-flex flex-column">
          <span class="d-block font-weight-semibold text--primary text-truncate">
            Contributor: {{ item.username }}
          </span>
          <small>{{ item.njangi_group_name }}</small>
          <small v-if="item.made_by_manager" class="blue--text">
            <v-icon x-small>mdi-account-tie</v-icon>
            Manager: {{ item.manager_name }}
          </small>
        </div>
      </template>

      <!-- Montant -->
      <template #[`item.amount`]="{ item }">
        {{ item.amount }}
      </template>

      <!-- Statut -->
      <template #[`item.status`]="{ item }">
        <v-chip
          small
          :color="item.confirmed ? 'success' : (item.cancelled ? 'error' : 'warning')"
          text-color="white"
        >
          {{ item.confirmed ? 'Confirmed' : (item.cancelled ? 'Cancelled' : 'Pending') }}
        </v-chip>
      </template>

      <!-- Cycle / Période -->
      <template #[`item.cycles`]="{ item }">
        <div v-if="item.cycles && item.cycles.length">
          <v-chip
            v-for="cycle in item.cycles"
            :key="cycle"
            x-small
            color="primary lighten-2"
            text-color="primary darken-2"
            class="mr-1"
          >
            {{ cycle }}
          </v-chip>
        </div>
        <span v-else class="grey--text">—</span>
      </template>

      <!-- Date -->
      <template #[`item.time_created`]="{ item }">
        {{ item.time_created }}
      </template>

      <!-- Actions -->
      <template #[`item.actions`]="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="item.confirmed && !item.cancelled"
              v-bind="attrs"
              v-on="on"
              small
              outlined
              color="error"
              :loading="reversingTransactionId === item.id"
              @click="confirmReverse(item)"
              class="reverse-btn"
            >
              <v-icon left>mdi-undo</v-icon>
              Reverse
            </v-btn>
          </template>
          <span>Annuler ce dépôt et retirer la contribution</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <!-- Dialogue de confirmation -->
    <v-dialog v-model="reverseDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="headline error--text">Reverse Transaction?</v-card-title>
        <v-card-text>
          <p>Are you sure you want to reverse this transaction?</p>
          <p class="font-weight-bold">
            {{ selectedTransaction ? selectedTransaction.username : '' }} —
            {{ selectedTransaction ? selectedTransaction.amount : '' }}
          </p>
          <p class="caption grey--text">
            This will remove the contribution from the system and the member will be considered as not having paid.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="reverseDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="reverseTransaction">Reverse</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mdiSquareEditOutline, mdiDotsVertical } from '@mdi/js'
import axios from 'axios'
import Spinner from '@/components/Spinner.vue'

export default {
  components: { Spinner },

  data() {
    return {
      headers: [
        { text: 'Name', value: 'username' },
        { text: 'Amount', value: 'amount' },
        { text: 'Status', value: 'status' },
        { text: 'Cycle', value: 'cycles' },   // colonne cycle
        { text: 'Date/Time', value: 'time_created' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      contribution_total: '',
      transactions: [],
      search: '',
      selectedStartDate: null,
      selectedEndDate: null,
      loading: false,
      reversingTransactionId: null,
      reverseDialog: false,
      selectedTransaction: null,

      icons: {
        mdiSquareEditOutline,
        mdiDotsVertical,
      },
    }
  },

  computed: {
    filteredTransactions() {
      return this.transactions.filter(tx => tx.njangi_group_name && tx.njangi_group_name !== '--- None ---')
    },
  },

  watch: {
    selectedStartDate() { this.saveDatesToLocalStorage() },
    selectedEndDate() { this.saveDatesToLocalStorage() },
  },

  async mounted() {
    document.title = 'Manager: Njangi | List of Transactions'
    this.loadDatesFromLocalStorage()
    await this.getTransactions()
  },

  methods: {
    loadDatesFromLocalStorage() {
      const savedStart = localStorage.getItem('njangi_request_transactions_start_date')
      const savedEnd = localStorage.getItem('njangi_request_transactions_end_date')
      if (savedStart && savedEnd) {
        this.selectedStartDate = savedStart
        this.selectedEndDate = savedEnd
      } else {
        const now = new Date()
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
        this.selectedStartDate = this.formatDate(startOfMonth)
        this.selectedEndDate = this.formatDate(now)
        this.saveDatesToLocalStorage()
      }
    },

    saveDatesToLocalStorage() {
      if (this.selectedStartDate) localStorage.setItem('njangi_request_transactions_start_date', this.selectedStartDate)
      if (this.selectedEndDate) localStorage.setItem('njangi_request_transactions_end_date', this.selectedEndDate)
    },

    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    async getTransactions() {
      if (this.selectedStartDate === '' || this.selectedStartDate === null) this.selectedStartDate = 'null'
      if (this.selectedEndDate === '' || this.selectedEndDate === null) this.selectedEndDate = 'null'
      this.loading = true
      try {
        const response = await axios.get(
          '/api/v1/manager/njangi/request-transactions/' + this.selectedStartDate + '/' + this.selectedEndDate + '/'
        )
        this.contribution_total = response.data['total_amount']
        this.transactions = response.data['response_data']
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loading = false
      }
    },

    confirmReverse(item) {
      this.selectedTransaction = item
      this.reverseDialog = true
    },

    async reverseTransaction() {
      if (!this.selectedTransaction) return
      const txId = this.selectedTransaction.id
      this.reversingTransactionId = txId
      this.reverseDialog = false

      try {
        await axios.post(
          `/api/v1/manager/njangi/change/requested_transaction/${txId}/`,
          { cancelledState: 'true' }   // ✅ Clé corrigée
        )
        this.$store.commit('setSnackBarMessage', '✅ Transaction reversed successfully')
        this.$store.commit('setSnackBarColor', 'success')
        this.$store.commit('setSnackBarTimeOut', 4000)
        this.$store.commit('activateSnackBar', true)
        await this.getTransactions() // Rafraîchir la liste
      } catch (error) {
        this.handleError(error)
      } finally {
        this.reversingTransactionId = null
        this.selectedTransaction = null
      }
    },

    handleError(error) {
      if (error.response) {
        this.$store.commit('setSnackBarMessage', error.response.status + ' ' + error.response.statusText)
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      } else {
        this.$store.commit('setSnackBarMessage', 'An error occurred, please check your internet connection and try again later.')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      }
    },
  },
}
</script>

<style scoped>
.reverse-btn {
  text-transform: none;
  letter-spacing: 0.3px;
  border-radius: 20px;
  padding: 0 12px;
  transition: all 0.2s ease;
}

.reverse-btn:hover {
  background-color: #ffebee !important;
  border-color: #c62828 !important;
  box-shadow: 0 2px 8px rgba(198, 40, 40, 0.2);
}
</style>
